import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { DAYS, today, weekday } from "@/lib/dates";
import { Card, ErrorBox, FormSheet, Loading, PageTitle, SubjectBadge } from "@/components/common";
import { Field, SubjectSelect } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8];

function SlotSheet({ slot, onClose, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState({ subject: "", teacher: "", room: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (slot) setForm({ subject: slot.subject || "", teacher: slot.teacher || "", room: slot.room || "" });
  }, [slot]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onClose(); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = (e) => {
    e.preventDefault();
    if (!form.subject) return toast.error("Scegli la materia");
    run(() => api("put", "/schedule", { ...form, day: slot.day, hour: slot.hour }), "Orario aggiornato");
  };
  return (
    <FormSheet open={!!slot} onOpenChange={(o) => !o && onClose()} title={slot ? `${DAYS[slot.day - 1]}, ${slot.hour}ª ora` : ""}>
      <form onSubmit={save} className="space-y-4" data-testid="slot-form">
        <Field label="Materia"><SubjectSelect value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="slot-subject" /></Field>
        <Field label="Docente (facoltativo)"><Input className="h-11" value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} data-testid="slot-teacher" /></Field>
        <Field label="Aula (facoltativo)"><Input className="h-11" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="slot-room" /></Field>
        <div className="flex gap-2">
          {slot?.id && <Button type="button" variant="outline" disabled={busy} className="h-12 flex-1 rounded-full" onClick={() => run(() => api("delete", `/schedule/${slot.id}`), "Ora svuotata")} data-testid="slot-clear">Svuota</Button>}
          <Button type="submit" disabled={busy} className="h-12 flex-1 rounded-full text-base" data-testid="slot-submit">Salva</Button>
        </div>
      </form>
    </FormSheet>
  );
}

export default function Orario() {
  const { isAdmin } = useMe();
  const { data, error, reload } = useFetch("/schedule");
  const wd = weekday(today());
  const [day, setDay] = useState(wd === 7 ? 1 : wd);
  const [editing, setEditing] = useState(null);

  if (error) return <ErrorBox text={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const slots = data.filter((s) => s.day === day);
  const maxHour = Math.max(5, ...data.map((s) => s.hour));
  const hours = isAdmin ? HOURS : HOURS.filter((h) => h <= maxHour);

  return (
    <div>
      <PageTitle title="Orario settimanale" subtitle={isAdmin ? "Tocca un'ora per modificarla" : "Le lezioni della settimana"} />
      <div className="no-scrollbar -mx-4 mb-5 flex gap-2 overflow-x-auto px-4 fade-up" data-testid="orario-day-tabs">
        {DAYS.map((d, i) => (
          <button key={d} onClick={() => setDay(i + 1)} data-testid={`orario-day-${i + 1}`}
            className={`shrink-0 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors active:scale-95 ${day === i + 1 ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-muted-foreground hover:bg-secondary"}`}>
            {d}{wd === i + 1 && " · oggi"}
          </button>
        ))}
      </div>
      <Card className="space-y-1 p-2 lg:max-w-2xl" data-testid="orario-day-list">
        {hours.map((h) => {
          const s = slots.find((x) => x.hour === h);
          const Row = isAdmin ? "button" : "div";
          return (
            <Row key={h} onClick={isAdmin ? () => setEditing(s || { day, hour: h }) : undefined} data-testid={`orario-slot-${day}-${h}`}
              className={`flex w-full items-center gap-3 rounded-xl p-2.5 text-left ${isAdmin ? "transition-colors hover:bg-secondary/70 active:scale-[0.99]" : ""}`}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-secondary font-heading font-bold">{h}ª</span>
              {s ? (
                <div className="min-w-0 flex-1">
                  <SubjectBadge subject={s.subject} />
                  {(s.teacher || s.room) && <p className="mt-0.5 truncate text-xs text-muted-foreground">{[s.teacher, s.room].filter(Boolean).join(" · ")}</p>}
                </div>
              ) : <span className="flex-1 text-sm text-muted-foreground">{isAdmin ? "Aggiungi materia" : "—"}</span>}
              {isAdmin && (s ? <Pencil className="h-4 w-4 text-muted-foreground" /> : <Plus className="h-4 w-4 text-muted-foreground" />)}
            </Row>
          );
        })}
      </Card>
      <SlotSheet slot={editing} onClose={() => setEditing(null)} onSaved={reload} />
    </div>
  );
}
